import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import { useIsDesktop } from "./scroll";
import { TruckGlyph } from "./transport";

// --- Buttery momentum scrolling (Lenis) — DESKTOP ONLY ---
// On touch devices Lenis hijacks native scrolling and causes stutter/rubber-banding
// ("glitchy" scroll on phones), so we leave mobile on native scroll.
export function SmoothScroll() {
  const reduce = useReducedMotion();
  const desktop = useIsDesktop();
  useEffect(() => {
    if (reduce || !desktop) return undefined;
    const lenis = new Lenis({ duration: 1.15, lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, [reduce, desktop]);
  return null;
}

// --- Cinematic preloader with a live % counter (once per session) ---
export function Preloader() {
  const [done, setDone] = useState(() => {
    try { return sessionStorage.getItem("ito-preloaded") === "1"; } catch { return true; }
  });
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (done) return undefined;
    document.body.style.overflow = "hidden";
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 2);
      setPct(Math.round(eased * 100));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          setDone(true);
          document.body.style.overflow = "";
          try { sessionStorage.setItem("ito-preloaded", "1"); } catch { /* ignore */ }
        }, 300);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); document.body.style.overflow = ""; };
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[200] bg-[#1A1A18] flex flex-col items-center justify-center"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }}
        >
          <motion.img
            src="/ito-logo.png" alt="ITO" className="w-20 h-20 mb-7"
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
          />
          <div className="font-heading font-black text-white text-6xl sm:text-7xl tabular-nums leading-none">{pct}%</div>
          <div className="mt-7 w-56 h-[3px] bg-white/15 rounded-full overflow-hidden">
            <div className="h-full bg-[#C84B31] transition-[width] duration-75" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/55">
            Indian Transport Organisation
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// --- Pinned "count up as you scroll" stats scene ---
function ScrollStat({ to, suffix, progress, from = 0, to2 = 0.75 }) {
  const [v, setV] = useState(0);
  useMotionValueEvent(progress, "change", (p) => {
    const t = Math.min(Math.max((p - from) / (to2 - from), 0), 1);
    const eased = 1 - Math.pow(1 - t, 3);
    setV(Math.round(eased * to));
  });
  const disp = v >= 1000 ? v.toLocaleString("en-IN") : v;
  return <>{disp}{suffix}</>;
}

export function CountScene({ stats, eyebrow, heading }) {
  const ref = useRef(null);
  // Count up while the section rises into view; finishes as it reaches centre
  // (never keeps ticking after it scrolls above the fold).
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });

  const inner = (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full">
      {eyebrow && (
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#D4A373] mb-4" data-testid="count-scene-eyebrow">{eyebrow}</p>
      )}
      {heading && (
        <h2 className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-[1.1] max-w-3xl mb-14">{heading}</h2>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 lg:gap-x-10 gap-y-12">
        {stats.map((s, i) => (
          <div key={s.label} className="border-l-2 border-[#C84B31]/70 pl-5" data-testid={`count-stat-${i}`}>
            <div className="font-heading font-black text-[2.75rem] sm:text-6xl lg:text-7xl text-white tracking-tighter tabular-nums leading-[0.95]">
              <ScrollStat to={s.value} suffix={s.suffix} progress={scrollYProgress} from={0.15 + i * 0.04} to2={0.6 + i * 0.04} />
            </div>
            <div className="mt-4 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-white/80 leading-snug">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section ref={ref} data-testid="count-scene" className="relative bg-[#1A1A18] text-white flex flex-col justify-center overflow-hidden py-24 sm:py-28 lg:py-32">
      <div
        className="animate-lane pointer-events-none"
        style={{
          position: "absolute", left: 0, right: 0, bottom: "12%", height: "2px", opacity: 0.14,
          backgroundImage: "repeating-linear-gradient(to right, #F9F8F6 0 26px, transparent 26px 52px)",
        }}
      />
      <div className="pointer-events-none absolute bottom-[6%] left-[5%] opacity-[0.06]">
        <TruckGlyph className="w-[380px] h-auto" style={{ color: "#F9F8F6" }} />
      </div>
      {inner}
    </section>
  );
}

// --- Scroll-scrubbed pinned horizontal gallery (desktop). The row of cards
// slides sideways as you scroll down, then releases. Mobile = swipe scroll. ---
export function PinnedGallery({ children }) {
  const items = Array.isArray(children) ? children : [children];
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(0);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);

  useEffect(() => {
    const calc = () => {
      if (trackRef.current) {
        setDist(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 64));
      }
    };
    calc();
    window.addEventListener("resize", calc);
    const t = setTimeout(calc, 400);
    return () => { window.removeEventListener("resize", calc); clearTimeout(t); };
  }, []);

  if (reduce || !isDesktop) {
    return (
      <div className="overflow-x-auto no-scrollbar" data-testid="pinned-gallery">
        <div className="flex gap-8 px-8 w-max">{items}</div>
      </div>
    );
  }

  return (
    <section ref={sectionRef} data-testid="pinned-gallery" className="relative" style={{ height: `${Math.round(items.length * 30)}vh` }}>
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <motion.div ref={trackRef} style={{ x }} className="flex gap-8 px-8 w-max will-change-transform">
          {items}
        </motion.div>
      </div>
    </section>
  );
}
